import { Activity, Bot, Eye, MousePointerClick, RefreshCw, UsersRound } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { fetchUsage } from "../api";
import { MetricCard } from "../components/MetricCard";
import { useDashboard } from "../context/DashboardContext";
import type { UsagePayload } from "../types";

function value(record: Record<string, number> | undefined, ...keys: string[]): number {
  for (const key of keys) {
    const candidate = Number(record?.[key]);
    if (Number.isFinite(candidate)) return candidate;
  }
  return 0;
}

function numericValue(candidate: unknown): number {
  const number = Number(candidate);
  return Number.isFinite(number) ? number : 0;
}

function formatChileDate(raw?: string): string {
  if (!raw) return "—";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-CL", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "America/Santiago",
  }).format(date);
}

function eventLabel(eventType?: string): string {
  const labels: Record<string, string> = {
    page_view: "Vista de página",
    gertrudis_attempt: "Intento de Gertrudis",
    gertrudis_attempt_blocked: "Intento en cooldown",
    gertrudis_seo_invoked: "Llamada SEO ejecutada",
    gertrudis_paid_invoked: "Llamada Paid ejecutada",
    feedback_submitted: "Feedback enviado",
  };
  return labels[eventType ?? ""] ?? eventType?.replaceAll("_", " ") ?? "—";
}

export function UsagePage() {
  const { session } = useDashboard();
  const [data, setData] = useState<UsagePayload | null>(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    void fetchUsage(days).then(setData);
  }, [days]);

  if (session && !session.canViewUsage) return <Navigate to="/" replace />;

  const kpis = data?.kpis;
  const funnel = data?.funnel;
  const attempts = value(funnel, "attempts_total", "attempts");
  const invoked = value(funnel, "invoked", "invoked_total");
  const cooldowns = value(funnel, "blocked", "blocked_total");
  const users = data?.by_user ?? [];
  const pages = data?.by_page ?? [];
  const revisits = data?.revisits ?? [];
  const recent = data?.recent ?? [];
  const funnelRows = [
    { label: "Intentos de llamada", count: attempts, tone: "blue" },
    { label: "Llamadas reales", count: invoked, tone: "good" },
    { label: "Intentos en cooldown", count: cooldowns, tone: "danger" },
  ];

  return (
    <div className="page">
      <section className="page-intro enter">
        <div>
          <span className="eyebrow">Producto · telemetría privada</span>
          <h1>Uso del dashboard</h1>
          <p>Actividad por persona, navegación e invocaciones de Gertrudis para el período seleccionado.</p>
        </div>
        <select className="compact-select" value={days} onChange={(event) => setDays(Number(event.target.value))}>
          <option value={7}>7 días</option>
          <option value={30}>30 días</option>
          <option value={90}>90 días</option>
        </select>
      </section>

      <section className="metric-grid">
        <MetricCard label="Usuarios" value={String(value(kpis, "users", "usuarios"))} note={`${days} días`} icon={UsersRound} />
        <MetricCard label="Sesiones" value={String(value(kpis, "sessions", "sesiones"))} note="en la ventana" icon={Activity} />
        <MetricCard label="Page views" value={String(value(kpis, "page_views", "pageViews"))} note="registradas" icon={Eye} />
        <MetricCard label="Eventos" value={String(value(kpis, "events", "eventos"))} note="registrados" icon={MousePointerClick} />
      </section>

      <section className="panel funnel-panel">
        <div className="panel-heading">
          <div>
            <span className="eyebrow orange">Gertrudis</span>
            <h2>Invocaciones de Gertrudis</h2>
            <p className="panel-copy">Relación entre intención de uso, llamada ejecutada y aplicación del cooldown.</p>
          </div>
          <Bot size={21} />
        </div>
        <div className="funnel-bars">
          {funnelRows.map(({ label, count, tone }) => {
            const width = attempts > 0 && count > 0 ? Math.max((count / attempts) * 100, 3) : 0;
            return (
              <div className="funnel-row" key={label}>
                <span>{label}</span>
                <div><i className={`tone-${tone}`} style={{ width: `${width}%` }} /></div>
                <strong>{count}</strong>
              </div>
            );
          })}
        </div>
      </section>

      <div className="usage-stack">
        <section className="panel usage-table-panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Personas</span>
              <h2>Actividad por persona</h2>
              <p className="panel-copy">Sesiones e invocaciones de Gertrudis dentro del período seleccionado.</p>
            </div>
            <span className="count-badge">{users.length}</span>
          </div>
          <div className="table-scroll">
            <table className="usage-table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th className="numeric">Sesiones</th>
                  <th>Última actividad (CL)</th>
                  <th className="numeric">Intentos</th>
                  <th className="numeric">Llamadas reales</th>
                  <th className="numeric">Cooldowns</th>
                  <th className="numeric">Eventos</th>
                </tr>
              </thead>
              <tbody>
                {users.map((row) => (
                  <tr key={row.user_email ?? "usuario"}>
                    <td className="primary-cell">{row.user_email ?? "—"}</td>
                    <td className="numeric">{numericValue(row.sesiones)}</td>
                    <td>{formatChileDate(row.ultima_actividad)}</td>
                    <td className="numeric">{numericValue(row.intentos_gertrudis)}</td>
                    <td className="numeric accent-good">{numericValue(row.llamadas_gertrudis)}</td>
                    <td className="numeric">{numericValue(row.cooldowns_gertrudis)}</td>
                    <td className="numeric">{numericValue(row.eventos)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && <p className="empty-table">La ventana seleccionada está lista para recibir actividad.</p>}
          </div>
        </section>

        <div className="usage-detail-grid">
          <section className="panel usage-table-panel">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Navegación</span>
                <h2>Vistas por página</h2>
                <p className="panel-copy">Páginas consultadas y sesiones asociadas.</p>
              </div>
              <span className="count-badge">{pages.length}</span>
            </div>
            <div className="table-scroll">
              <table className="usage-table compact">
                <thead>
                  <tr>
                    <th>Página</th>
                    <th className="numeric">Vistas</th>
                    <th className="numeric">Sesiones</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.map((row) => (
                    <tr key={row.page ?? "página"}>
                      <td className="primary-cell">{row.page ?? "—"}</td>
                      <td className="numeric">{numericValue(row.vistas)}</td>
                      <td className="numeric">{numericValue(row.sesiones)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pages.length === 0 && <p className="empty-table">La ventana seleccionada está lista para recibir vistas.</p>}
            </div>
          </section>

          <section className="panel usage-table-panel">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Frecuencia</span>
                <h2>Revisitas por cliente</h2>
                <p className="panel-copy">Combinaciones de página y cliente con actividad recurrente.</p>
              </div>
              <span className="count-badge">{revisits.length}</span>
            </div>
            <div className="table-scroll">
              <table className="usage-table compact">
                <thead>
                  <tr>
                    <th>Página</th>
                    <th>Cliente</th>
                    <th className="numeric">Vistas</th>
                    <th className="numeric">Sesiones</th>
                  </tr>
                </thead>
                <tbody>
                  {revisits.map((row, index) => (
                    <tr key={`${row.page}-${row.client_slug}-${index}`}>
                      <td className="primary-cell">{row.page ?? "—"}</td>
                      <td>{row.client_slug ?? "—"}</td>
                      <td className="numeric">{numericValue(row.vistas)}</td>
                      <td className="numeric">{numericValue(row.sesiones)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!data && (
                <p className="empty-table" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                  <RefreshCw size={14} className="spin" />
                  <span>Consultando recurrencia en BigQuery...</span>
                </p>
              )}
              {data && revisits.length === 0 && <p className="empty-table">La ventana seleccionada está lista para registrar recurrencia.</p>}
            </div>
          </section>
        </div>

        <section className="panel usage-table-panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Trazabilidad</span>
              <h2>Actividad reciente</h2>
              <p className="panel-copy">Los 50 eventos más recientes del período seleccionado.</p>
            </div>
            <span className="count-badge">{recent.length}</span>
          </div>
          <div className="table-scroll">
            <table className="usage-table recent-table">
              <thead>
                <tr>
                  <th>Fecha (CL)</th>
                  <th>Usuario</th>
                  <th>Evento</th>
                  <th>Página</th>
                  <th>Cliente</th>
                  <th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((row, index) => (
                  <tr key={`${row.timestamp}-${row.user_email}-${index}`}>
                    <td>{formatChileDate(row.timestamp)}</td>
                    <td className="primary-cell">{row.user_email ?? "—"}</td>
                    <td>{eventLabel(row.event_type)}</td>
                    <td>{row.page ?? "—"}</td>
                    <td>{row.client_slug ?? "—"}</td>
                    <td>{row.detail ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!data && (
              <p className="empty-table" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                <RefreshCw size={14} className="spin" />
                <span>Consultando eventos recientes en BigQuery...</span>
              </p>
            )}
            {data && recent.length === 0 && <p className="empty-table">La ventana seleccionada está lista para recibir eventos.</p>}
          </div>
        </section>
      </div>
    </div>
  );
}
